import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  X, MessageCircle, User, VolumeX, Heart, Share2, 
  Users, Zap, Eye, ChevronDown 
} from 'lucide-react';

const UserProfileModal = ({ user, currentTheme, onClose, onMessage, t, isVisible }) => {
  if (!isVisible || !user) return null;

  // Calculate level from XP
  const level = Math.floor((user.xp || 1000) / 200) + 1;
  const currentLevelXP = (level - 1) * 200;
  const nextLevelXP = level * 200;
  const progressXP = (user.xp || 1000) - currentLevelXP;
  const progressPercentage = (progressXP / 200) * 100;

  // Mock data for posts and mutual connections
  const mockPosts = [
    {
      id: 1,
      content: "Just finished a great workout session! 💪",
      timestamp: "2 hours ago",
      likes: 12,
      type: "achievement"
    },
    {
      id: 2, 
      content: "Working on some exciting projects today 🚀",
      timestamp: "1 day ago",
      likes: 8,
      type: "update"
    }
  ];

  const mockMutualConnections = [
    { name: "Sarah Chen", avatar: "S" },
    { name: "Alex Kim", avatar: "A" },
    { name: "Dev Team", avatar: "D", isGroup: true }
  ];

  const handleSwipeDown = (event, info) => {
    if (info.offset.y > 100 && info.velocity.y > 0) {
      onClose();
    }
  };

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.3 }}
        className="fixed inset-0 z-50 flex items-center justify-center p-4"
        style={{ 
          background: 'rgba(255, 255, 255, 0.1)',
          backdropFilter: 'blur(20px)',
          WebkitBackdropFilter: 'blur(20px)'
        }}
        onClick={onClose}
      >
        <motion.div
          initial={{ scale: 0.8, opacity: 0, y: 50 }}
          animate={{ scale: 1, opacity: 1, y: 0 }}
          exit={{ scale: 0.8, opacity: 0, y: 50 }}
          transition={{ 
            type: "spring",
            damping: 25,
            stiffness: 300,
            duration: 0.4
          }}
          drag="y"
          dragConstraints={{ top: 0, bottom: 200 }}
          onDragEnd={handleSwipeDown}          className="relative w-full max-w-md mx-auto rounded-3xl overflow-hidden"
          style={{
            background: 'rgba(15, 23, 42, 0.95)',
            backdropFilter: 'blur(40px)',
            WebkitBackdropFilter: 'blur(40px)',
            border: '1px solid rgba(255, 255, 255, 0.2)',
            boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.8)'
          }}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Drag Indicator */}
          <div className="flex justify-center pt-3 pb-2">
            <div className="w-12 h-1 bg-white/20 rounded-full"></div>
          </div>

          {/* Close Button */}
          <button
            onClick={onClose}
            className="absolute top-4 right-4 z-10 p-2 rounded-full bg-black/20 hover:bg-black/40 transition-colors"
          >
            <X size={20} className="text-white" />
          </button>

          {/* Profile Header */}
          <div className="relative px-6 pt-4 pb-8">
            {/* Avatar with Glow */}
            <div className="flex justify-center mb-4">
              <div className="relative">
                {/* Glow Effect */}
                <div 
                  className="absolute inset-0 rounded-full blur-xl"
                  style={{
                    background: 'radial-gradient(circle, rgba(139, 92, 246, 0.6) 0%, rgba(236, 72, 153, 0.4) 50%, transparent 70%)',
                    transform: 'scale(1.5)'
                  }}
                ></div>
                
                {/* Avatar Circle */}
                <div className="relative w-24 h-24 rounded-full bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center border-2 border-white/20">
                  <span className="text-3xl font-bold text-white">
                    {user.avatar || user.name.charAt(0).toUpperCase()}
                  </span>
                </div>

                {/* Online Status */}
                {user.isOnline && (
                  <div className="absolute -bottom-1 -right-1 w-6 h-6 bg-green-500 rounded-full border-3 border-slate-800 flex items-center justify-center">
                    <div className="w-2 h-2 bg-white rounded-full"></div>
                  </div>
                )}
              </div>
            </div>

            {/* Name and Level */}
            <div className="text-center mb-4">
              <h2 className="text-2xl font-bold text-white mb-1">{user.name}</h2>
              <p className="text-gray-300 text-sm mb-3">Level {level} • {user.lastActive}</p>
              
              {/* Compatibility Score */}
              <div className="inline-flex items-center px-3 py-1 rounded-full bg-gradient-to-r from-purple-500/20 to-pink-500/20 border border-purple-400/30">
                <span className="text-lg mr-1">🧬</span>
                <span className="text-purple-300 font-medium">{user.compatibility || 87}% match</span>
              </div>
            </div>

            {/* XP Progress Bar */}
            <div className="mb-6">
              <div className="flex justify-between items-center mb-2">
                <span className="text-sm text-gray-400">XP Progress</span>
                <span className="text-sm text-purple-300">{user.xp || 1000} / {nextLevelXP}</span>
              </div>
              <div className="w-full bg-gray-700 rounded-full h-2 overflow-hidden">
                <motion.div
                  initial={{ width: 0 }}
                  animate={{ width: `${progressPercentage}%` }}
                  transition={{ duration: 1, delay: 0.5 }}
                  className="h-full bg-gradient-to-r from-purple-500 to-pink-500 rounded-full"
                ></motion.div>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="px-6 pb-6">
            <div className="grid grid-cols-3 gap-3 mb-6">
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => onMessage && onMessage(user.id)}
                className="flex flex-col items-center p-4 rounded-2xl bg-gradient-to-r from-blue-500/20 to-cyan-500/20 border border-blue-400/30 hover:border-blue-300/50 transition-all"
              >
                <MessageCircle size={24} className="text-blue-300 mb-2" />
                <span className="text-xs text-blue-200 font-medium">Message</span>
              </motion.button>

              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="flex flex-col items-center p-4 rounded-2xl bg-gradient-to-r from-purple-500/20 to-indigo-500/20 border border-purple-400/30 hover:border-purple-300/50 transition-all"
              >
                <User size={24} className="text-purple-300 mb-2" />
                <span className="text-xs text-purple-200 font-medium">Profile</span>
              </motion.button>

              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="flex flex-col items-center p-4 rounded-2xl bg-gradient-to-r from-red-500/20 to-pink-500/20 border border-red-400/30 hover:border-red-300/50 transition-all"
              >
                <VolumeX size={24} className="text-red-300 mb-2" />
                <span className="text-xs text-red-200 font-medium">Mute</span>
              </motion.button>
            </div>
          </div>

          {/* Recent Posts */}
          <div className="px-6 pb-4">
            <h3 className="text-lg font-semibold text-white mb-4 flex items-center">
              <Zap size={18} className="mr-2 text-yellow-400" />
              Recent Activity
            </h3>
            <div className="space-y-3 max-h-40 overflow-y-auto">
              {mockPosts.map((post) => (
                <div key={post.id} className="p-3 rounded-xl bg-white/5 border border-white/10">
                  <p className="text-sm text-gray-200 mb-2">{post.content}</p>
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-gray-400">{post.timestamp}</span>
                    <div className="flex items-center text-gray-400">
                      <Heart size={12} className="mr-1" />
                      <span className="text-xs">{post.likes}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Mutual Connections */}
          <div className="px-6 pb-6">
            <h3 className="text-lg font-semibold text-white mb-4 flex items-center">
              <Users size={18} className="mr-2 text-green-400" />
              Mutual Connections
            </h3>
            <div className="flex items-center space-x-3">
              {mockMutualConnections.map((connection, index) => (
                <div key={index} className="flex flex-col items-center">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold text-white ${
                    connection.isGroup 
                      ? 'bg-gradient-to-br from-amber-500 to-orange-500' 
                      : 'bg-gradient-to-br from-blue-500 to-purple-500'
                  }`}>
                    {connection.avatar}
                  </div>
                  <span className="text-xs text-gray-300 mt-1 max-w-[3rem] truncate">
                    {connection.name}
                  </span>
                </div>
              ))}
              <div className="flex items-center text-gray-400">
                <span className="text-xs">+2 more</span>
              </div>
            </div>
          </div>

          {/* Bottom Gesture Hint */}
          <div className="flex justify-center pb-4">
            <div className="flex items-center text-gray-500 text-xs">
              <ChevronDown size={14} className="mr-1" />
              Swipe down to close
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default UserProfileModal;
